<template>
  <v-container fluid>
    <v-card class="mx-auto pa-4">
      <p class="display-1 text--primary">Category Keywords</p>
      <p class="text--secondary">
        Search terms set on an Acumatica category apply to that category's CAT code and every CAT code beneath it.
        They are combined with each CAT code's own terms and its Part Group's terms on the next scan.
      </p>

      <v-row>
        <v-col cols="12" md="6">
          <v-text-field v-model="searchText" label="Find a category by name, ID, or CAT code" prepend-inner-icon="mdi-magnify"
            clearable hide-details :loading="searching" />

          <v-list v-if="searchResults.length > 0" dense class="search-results mt-2">
            <v-list-item v-for="result in searchResults" :key="result.categoryID" @click="selectCategory(result.categoryID)">
              <v-list-item-content>
                <v-list-item-title>{{ result.description }}</v-list-item-title>
                <v-list-item-subtitle>{{ result.catCodeString }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <v-treeview class="mt-4 tree" dense activatable :items="tree" :load-children="loadChildren" item-key="categoryID"
            item-text="description" :active="active" :open.sync="open" @update:active="onActiveChanged" transition>
            <template v-slot:append="{ item }">
              <v-chip v-if="item.isMarketScoutCatCode" x-small label outlined class="mr-1" :href="catCodeHref(item.catCodeID)"
                target="_blank" rel="noopener" title="Edit CatCode in a new tab" @click.stop>CatCode</v-chip>
              <v-chip v-if="item.wordsToInclude" x-small label color="success" dark class="mr-1" :title="item.wordsToInclude">+ {{ item.wordsToInclude }}</v-chip>
              <v-chip v-if="item.wordsToExclude" x-small label color="error" dark :title="item.wordsToExclude">&minus; {{ item.wordsToExclude }}</v-chip>
            </template>
          </v-treeview>
        </v-col>

        <v-col cols="12" md="6">
          <v-card v-if="detail" outlined class="pa-4 editor">
            <v-card-title class="px-0 pt-0">{{ detail.description }}</v-card-title>
            <v-card-subtitle class="px-0">CAT-{{ detail.categoryID }}</v-card-subtitle>

            <v-text-field label="Words to include" v-model="wordsToInclude" counter="500" maxlength="500"
              hint="Space-separated. Added to this category and every category beneath it." persistent-hint />
            <v-text-field label="Words to exclude" v-model="wordsToExclude" counter="500" maxlength="500" class="mt-4"
              hint="Space-separated, without a leading '-'." persistent-hint />

            <div class="mt-4">
              <v-btn color="primary" :loading="saving" @click="save">
                <v-icon left>mdi-content-save</v-icon>
                Save
              </v-btn>
            </div>

            <v-divider class="my-4" />

            <div class="subtitle-2">MarketScout CatCode</div>
            <div v-if="detail.catCodeID">
              <a :href="catCodeHref(detail.catCodeID)" target="_blank" rel="noopener">{{ detail.catCodeString }}</a>
              is a MarketScout CatCode.
            </div>
            <div v-else class="text--secondary">{{ detail.catCodeString }} is not a MarketScout CatCode.</div>

            <div class="mt-2">
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn color="primary" outlined class="mr-2" :loading="catCodeBusy" v-bind="attrs" v-on="on">
                    <v-icon left>mdi-plus</v-icon>
                    Add CatCode
                    <v-icon right>mdi-menu-down</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item :disabled="!!detail.catCodeID" @click="openAddDialog('Self')">
                    <v-list-item-title>Add for this category only</v-list-item-title>
                  </v-list-item>
                  <v-list-item :disabled="!detail.hasChildren" @click="openAddDialog('Leaves')">
                    <v-list-item-title>Add for all leaf categories under this category</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn color="error" outlined :loading="catCodeBusy" v-bind="attrs" v-on="on">
                    <v-icon left>mdi-delete</v-icon>
                    Remove CatCode
                    <v-icon right>mdi-menu-down</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item :disabled="!detail.catCodeID" @click="removeCatCodes('Self')">
                    <v-list-item-title>Remove from this category only</v-list-item-title>
                  </v-list-item>
                  <v-list-item :disabled="!detail.catCodeID && !detail.hasChildren" @click="removeCatCodes('SelfAndDescendants')">
                    <v-list-item-title>Remove from this category and all categories under it</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <v-divider class="my-4" />

            <div class="subtitle-2">Inherited from parent categories</div>
            <div v-if="!detail.inheritedWordsToInclude && !detail.inheritedWordsToExclude" class="text--secondary">None</div>
            <div v-else>
              <span v-if="detail.inheritedWordsToInclude">Include: <strong>{{ detail.inheritedWordsToInclude }}</strong></span>
              <br v-if="detail.inheritedWordsToInclude && detail.inheritedWordsToExclude" />
              <span v-if="detail.inheritedWordsToExclude">Exclude: <strong>{{ detail.inheritedWordsToExclude }}</strong></span>
            </div>

            <v-simple-table dense class="mt-2">
              <thead>
                <tr>
                  <th>Ancestor</th>
                  <th>Include</th>
                  <th>Exclude</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="ancestor in detail.ancestors" :key="ancestor.categoryID">
                  <td><a @click="selectCategory(ancestor.categoryID)">{{ ancestor.description }}</a></td>
                  <td>{{ ancestor.wordsToInclude }}</td>
                  <td>{{ ancestor.wordsToExclude }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
          <p v-else class="text--secondary">Select a category to view or edit its search terms.</p>
        </v-col>
      </v-row>
    </v-card>

    <v-dialog v-model="addDialog" max-width="500">
      <v-card>
        <v-card-title>{{ addScope === 'Leaves' ? 'Add CatCodes for leaf categories' : 'Add CatCode' }}</v-card-title>
        <v-card-subtitle v-if="detail">{{ detail.description }}</v-card-subtitle>
        <v-card-text>
          <v-form ref="addForm">
            <v-select label="Part Group" v-model="addPartGroupID" :items="partGroups" item-text="name" item-value="id"
              :rules="[v => !!v || 'Part Group is required']" />
            <v-text-field label="Days between scans" v-model.number="addDaysBetweenScans" type="number" min="1"
              :rules="[v => v > 0 || 'Must be greater than 0']" />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="addDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="catCodeBusy" @click="addCatCodes">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmationDialog ref="confirm" />
  </v-container>
</template>

<script>
import { CategoryKeywordsService } from '../services/api/categoryKeywords';
import { PartGroupsService } from '../services/api/partGroups';
const apiCategoryKeywords = new CategoryKeywordsService();
const apiPartGroups = new PartGroupsService();

const MAX_LISTED_CATCODES = 20;

export default {
  name: 'CategoryKeywords',
  components: {
    ConfirmationDialog: () => import('../components/confirmation-dialog'),
  },
  data() {
    return {
      tree: [],
      nodesByID: {},
      active: [],
      open: [],
      detail: null,
      wordsToInclude: '',
      wordsToExclude: '',
      saving: false,
      searchText: '',
      searchTimeout: null,
      searching: false,
      searchResults: [],
      catCodeBusy: false,
      partGroups: [],
      addDialog: false,
      addScope: 'Self',
      addPartGroupID: null,
      addDaysBetweenScans: 7,
    };
  },

  watch: {
    searchText(text) {
      clearTimeout(this.searchTimeout);
      if (!text || !text.trim()) {
        this.searchResults = [];
        return;
      }
      this.searchTimeout = setTimeout(() => this.search(text.trim()), 400);
    },
  },

  async mounted() {
    this.tree = this.toNodes(await apiCategoryKeywords.getChildren(0));
  },

  methods: {
    toNodes(categories) {
      return categories.map(category => {
        const node = { ...category };
        // An empty children array tells v-treeview to lazy-load this node.
        if (category.hasChildren) {
          node.children = [];
        }
        this.nodesByID[category.categoryID] = node;
        return node;
      });
    },

    async loadChildren(node) {
      const children = await apiCategoryKeywords.getChildren(node.categoryID);
      node.children.push(...this.toNodes(children));
    },

    onActiveChanged(active) {
      this.active = active;
      if (active.length > 0 && (!this.detail || this.detail.categoryID !== active[0])) {
        this.loadDetail(active[0]);
      }
    },

    async selectCategory(categoryID) {
      await this.loadDetail(categoryID);
      await this.revealCategory(categoryID, this.detail.ancestors);
    },

    async revealCategory(categoryID, ancestors) {
      // Ancestors come back parent first, so walk them from the root down.
      const path = [...ancestors].reverse().map(a => a.categoryID);
      for (const id of path) {
        const node = this.nodesByID[id];
        if (node && node.children && node.children.length === 0) {
          await this.loadChildren(node);
        }
      }

      this.open = [...new Set([...this.open, ...path])];
      this.active = [categoryID];

      // Wait for the expand transition to finish so the node is at its final position.
      setTimeout(() => {
        const el = this.$el.querySelector('.tree .v-treeview-node--active');
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 300);
    },

    async search(text) {
      this.searching = true;
      try {
        this.searchResults = await apiCategoryKeywords.search(text);
      } finally {
        this.searching = false;
      }
    },

    async loadDetail(categoryID) {
      this.detail = await apiCategoryKeywords.getCategory(categoryID);
      this.wordsToInclude = this.detail.wordsToInclude || '';
      this.wordsToExclude = this.detail.wordsToExclude || '';
    },

    async save() {
      this.saving = true;
      try {
        this.detail = await apiCategoryKeywords.saveCategory(this.detail.categoryID, {
          wordsToInclude: this.wordsToInclude,
          wordsToExclude: this.wordsToExclude,
        });
        this.wordsToInclude = this.detail.wordsToInclude || '';
        this.wordsToExclude = this.detail.wordsToExclude || '';

        const node = this.nodesByID[this.detail.categoryID];
        if (node) {
          node.wordsToInclude = this.detail.wordsToInclude;
          node.wordsToExclude = this.detail.wordsToExclude;
        }

        this.$notify({ type: 'success', title: 'Category keywords saved' });
      } finally {
        this.saving = false;
      }
    },

    catCodeHref(catCodeID) {
      return this.$router.resolve({ path: `/catCodes/edit/${catCodeID}` }).href;
    },

    async openAddDialog(scope) {
      this.addScope = scope;
      this.addDialog = true;
      if (this.partGroups.length === 0) {
        this.partGroups = (await apiPartGroups.getPartGroups()).items;
      }
    },

    async addCatCodes() {
      if (!this.$refs.addForm.validate()) {
        return;
      }

      const categoryID = this.detail.categoryID;
      const scope = this.addScope;
      const settings = { partGroupID: this.addPartGroupID, daysBetweenScans: this.addDaysBetweenScans };

      this.catCodeBusy = true;
      try {
        if (scope === 'Leaves') {
          const preview = await apiCategoryKeywords.addCatCodes(categoryID, scope, settings, true);
          if (!(await this.confirmChange('Add CatCodes', 'add', preview))) {
            return;
          }
        }

        const result = await apiCategoryKeywords.addCatCodes(categoryID, scope, settings);
        this.addDialog = false;
        this.applyCatCodeChanges(result);
        this.notifyResult('added', result);
      } finally {
        this.catCodeBusy = false;
      }
    },

    async removeCatCodes(scope) {
      const categoryID = this.detail.categoryID;

      this.catCodeBusy = true;
      try {
        const preview = await apiCategoryKeywords.removeCatCodes(categoryID, scope, true);
        if (!(await this.confirmChange('Remove CatCodes', 'remove', preview, 'error'))) {
          return;
        }

        const result = await apiCategoryKeywords.removeCatCodes(categoryID, scope);
        this.applyCatCodeChanges(result);
        this.notifyResult('removed', result);
      } finally {
        this.catCodeBusy = false;
      }
    },

    async confirmChange(title, verb, preview, okColor = 'primary') {
      if (preview.catCodes.length === 0) {
        this.notifyResult(verb === 'add' ? 'added' : 'removed', preview);
        return false;
      }

      // CatCode strings are server-generated "CAT-<number>" values, so they are safe to render as HTML.
      const listed = preview.catCodes.slice(0, MAX_LISTED_CATCODES).map(c => c.catCodeString).join(', ');
      const more = preview.catCodes.length > MAX_LISTED_CATCODES ? `, and ${preview.catCodes.length - MAX_LISTED_CATCODES} more` : '';
      let message = `This will ${verb} <strong>${preview.catCodes.length}</strong> CatCode(s): ${listed}${more}.`;
      if (preview.skipped.length > 0) {
        message += `<br /><br />${preview.skipped.length} will be skipped.`;
      }

      return this.$refs.confirm.open(title, message, { okText: title, okColor, width: 500 });
    },

    applyCatCodeChanges(result) {
      for (const catCode of result.catCodes) {
        const node = this.nodesByID[catCode.categoryID];
        if (node) {
          node.catCodeID = catCode.catCodeID;
          node.isMarketScoutCatCode = !!catCode.catCodeID;
        }
        if (this.detail && this.detail.categoryID === catCode.categoryID) {
          this.detail.catCodeID = catCode.catCodeID;
        }
      }
    },

    notifyResult(pastVerb, result) {
      const skippedReasons = [...new Set(result.skipped.map(s => s.reason))].join(' ');
      const notification = {
        type: result.catCodes.length > 0 ? 'success' : 'warn',
        title: `${result.catCodes.length} CatCode(s) ${result.dryRun ? 'to be ' : ''}${pastVerb}`,
      };
      if (result.skipped.length > 0) {
        notification.text = `${result.skipped.length} skipped. ${skippedReasons}`;
        notification.duration = 15 * 1000;
      }
      this.$notify(notification);
    },
  },
};
</script>

<style scoped>
.tree {
  max-height: 70vh;
  overflow-y: auto;
}

.search-results {
  max-height: 240px;
  overflow-y: auto;
}

.editor {
  position: sticky;
  top: 16px;
}
</style>
